import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PharmacyLayout } from './PharmacyLayout';
import {
  usePharmacyPayables,
  useCreatePharmacySupplierPayment,
  usePharmacySuppliers,
  useDeletePharmacySupplierInvoices,
} from '../../services/httpServices/queries';
import type { PharmacySupplierInvoice, PharmacyPaymentMethod } from '../../services/httpServices/pharmacyPurchasingService';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { PharmacyInvoiceDetailsModal } from './PharmacyInvoiceDetailsModal';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import {
  Loader2,
  AlertCircle,
  Wallet,
  DollarSign,
  X,
  CreditCard,
  Search,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const statusStyles: Record<string, string> = {
  UNPAID: 'bg-danger/10 text-danger',
  PARTIAL: 'bg-warning/10 text-warning',
  PAID: 'bg-success/10 text-success',
  OVERDUE: 'bg-danger/10 text-danger',
};

const methods: PharmacyPaymentMethod[] = ['CASH', 'CARD', 'BANK_TRANSFER', 'CHEQUE', 'MOBILE_BANKING', 'OTHER'];

const PAGE_SIZES = [10, 25, 50, 100];

export function PharmacyPayables() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);

  const { data, isLoading, error } = usePharmacyPayables(
    status || supplierId ? { status: status || undefined, supplierId: supplierId || undefined } : undefined,
  );
  const { data: suppliersData } = usePharmacySuppliers();
  const deleteMutation = useDeletePharmacySupplierInvoices();

  const summary = data?.data;
  const suppliers = suppliersData?.data || [];

  const invoices = useMemo(() => {
    const list = summary?.invoices || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(
      (inv) =>
        inv.invoiceNumber?.toLowerCase().includes(q) ||
        inv.supplier?.name?.toLowerCase().includes(q) ||
        inv.grn?.grnNumber?.toLowerCase().includes(q),
    );
  }, [summary, search]);

  // Reset to the first page whenever the result set changes.
  useEffect(() => {
    setPage(1);
  }, [search, status, supplierId, pageSize]);

  const totalPages = Math.max(1, Math.ceil(invoices.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedInvoices = useMemo(
    () => invoices.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [invoices, currentPage, pageSize],
  );

  const allOnPageSelected =
    pagedInvoices.length > 0 && pagedInvoices.every((inv) => selected.has(inv.id));

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pagedInvoices.forEach((inv) => next.delete(inv.id));
      else pagedInvoices.forEach((inv) => next.add(inv.id));
      return next;
    });

  const balanceOf = (inv: PharmacySupplierInvoice) =>
    Number(
      inv.balance ?? Number(inv.amount) - Number(inv.paidAmount) - Number(inv.creditedAmount ?? 0),
    );

  const hasFilters = Boolean(search || status || supplierId);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setSupplierId('');
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const res = await deleteMutation.mutateAsync(ids);
      const blocked = res.data?.blocked?.length ?? 0;
      const deleted = res.data?.deleted?.length ?? 0;
      if (blocked > 0) {
        toast.warning(`${deleted} deleted · ${blocked} skipped (they have recorded payments)`);
      } else {
        toast.success(`${deleted} invoice${deleted === 1 ? '' : 's'} deleted`);
      }
      setSelected(new Set());
      setConfirmDelete(false);
    } catch {
      // handled globally
    }
  };

  const [payInvoice, setPayInvoice] = useState<PharmacySupplierInvoice | null>(null);

  return (
    <PharmacyLayout
      title="Supplier Payables"
      description="Payables are generated from goods receipts. Track what the pharmacy owes suppliers, find paid invoices and record payments"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-text-muted text-sm">
            <Wallet size={16} /> Total Outstanding
          </div>
          <div className="text-2xl font-bold text-text mt-1">
            {Number(summary?.totalOutstanding || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-danger text-sm">
            <AlertCircle size={16} /> Overdue
          </div>
          <div className="text-2xl font-bold text-danger mt-1">
            {Number(summary?.totalOverdue || 0).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by invoice, supplier or GRN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All statuses</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PARTIAL">Partial</option>
            <option value="PAID">Paid</option>
            <option value="OVERDUE">Overdue</option>
          </select>
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All suppliers</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        {(hasFilters || selected.size > 0) && (
          <div className="flex items-center justify-between gap-3 mt-3">
            {hasFilters ? (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-lg text-xs text-text-muted hover:text-text hover:bg-background"
              >
                <X size={14} /> Clear filters
              </button>
            ) : (
              <span />
            )}
            {selected.size > 0 && (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-danger/10 text-danger rounded-lg text-xs font-medium hover:bg-danger/20"
              >
                <Trash2 size={14} /> Delete {selected.size} selected
              </button>
            )}
          </div>
        )}
      </div>

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all on page"
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Receipt (GRN)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Due Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Balance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load payables
                  </td>
                </tr>
              ) : pagedInvoices.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-text-muted">
                    No invoices match your filters.
                  </td>
                </tr>
              ) : (
                pagedInvoices.map((inv) => {
                  const balance = balanceOf(inv);
                  return (
                    <tr key={inv.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(inv.id)}
                          onChange={() => toggleSelect(inv.id)}
                          aria-label={`Select invoice ${inv.invoiceNumber}`}
                          className="rounded border-border text-primary focus:ring-primary"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setDetailInvoiceId(inv.id)}
                          className="font-mono text-primary hover:underline"
                          title="View invoice details"
                        >
                          {inv.invoiceNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-text">{inv.supplier?.name || '—'}</td>
                      <td className="px-4 py-3">
                        {inv.grn?.grnNumber ? (
                          <button
                            onClick={() => navigate(`/pharmacy/receipts/${inv.grn!.id}`)}
                            className="font-mono text-primary hover:underline"
                            title="View goods receipt"
                          >
                            {inv.grn.grnNumber}
                          </button>
                        ) : inv.grnId ? (
                          <button
                            onClick={() => navigate(`/pharmacy/receipts/${inv.grnId}`)}
                            className="text-primary hover:underline"
                          >
                            View
                          </button>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-muted">
                        {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-text">{Number(inv.amount).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-medium text-text">{balance.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[inv.status]}`}>
                          {inv.isOverdue && inv.status !== 'PAID' ? 'OVERDUE' : inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDetailInvoiceId(inv.id)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Invoice details"
                          >
                            <Eye size={16} />
                          </button>
                          {balance > 0 ? (
                            <button
                              onClick={() => setPayInvoice(inv)}
                              className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 flex items-center gap-1.5"
                            >
                              <DollarSign size={14} /> Pay
                            </button>
                          ) : (
                            <span className="px-2 py-1 text-xs text-text-muted">Settled</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && !error && invoices.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border text-sm">
            <div className="text-text-muted">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, invoices.length)} of {invoices.length}
            </div>
            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1.5 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="p-1.5 rounded-lg border border-border text-text-muted hover:text-text hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="px-2 text-text-muted">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-1.5 rounded-lg border border-border text-text-muted hover:text-text hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${selected.size} invoice${selected.size === 1 ? '' : 's'}?`}
        description="Invoices that already have recorded payments will be skipped. This action cannot be undone."
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {payInvoice && (
        <PaymentModal invoice={payInvoice} onClose={() => setPayInvoice(null)} />
      )}

      {detailInvoiceId && (
        <PharmacyInvoiceDetailsModal invoiceId={detailInvoiceId} onClose={() => setDetailInvoiceId(null)} />
      )}
    </PharmacyLayout>
  );
}

function PaymentModal({ invoice, onClose }: { invoice: PharmacySupplierInvoice; onClose: () => void }) {
  const mutation = useCreatePharmacySupplierPayment();
  const balance = Number(
    invoice.balance ??
      Number(invoice.amount) - Number(invoice.paidAmount) - Number(invoice.creditedAmount ?? 0),
  );
  const [amount, setAmount] = useState(balance);
  const [method, setMethod] = useState<PharmacyPaymentMethod>('CASH');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const inputClass =
    'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await mutation.mutateAsync({ invoiceId: invoice.id, amount: Number(amount), method, reference: reference || undefined, notes: notes || undefined });
      toast.success('Payment recorded');
      onClose();
    } catch {
      // handled globally
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">Record Payment</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background text-text-muted">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-4">
          <div className="text-sm">
            <div className="font-medium text-text">{invoice.invoiceNumber}</div>
            <div className="text-text-muted">
              {invoice.supplier?.name} • Balance {balance.toFixed(2)}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Amount *</label>
            <ValidatedNumberInput
              max={balance}
              value={amount}
              onChange={setAmount}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value as PharmacyPaymentMethod)} className={inputClass}>
              {methods.map((m) => (
                <option key={m} value={m}>
                  {m.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Reference</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Notes</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />
          </div>
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-text-muted hover:text-text">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 size={16} className="animate-spin" />}
              Record Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
